<template>
  <div class="my-devices">
    <!-- 数据展示区域 -->
    <div class="data-section">
      <div class="section-header">
        <div>
          <span style="font-size: 14px; color: #333">广告管理</span>
        </div>
        <div class="toolbar">
          <el-button v-if="userRole === 0" type="primary" @click="openAddDialog">新增</el-button>

          <!-- 刷新按钮 -->
          <el-button type="text" @click="refreshData">
            <el-icon><Refresh /></el-icon>
          </el-button>

          <!-- 密度设置 -->
          <el-popover
            placement="bottom"
            width="160"
            v-model:visible="densityDialogVisible"
          >
            <div class="popover-content">
              <el-button
                class="density-button"
                :type="rowHeight === '160px' ? 'primary' : ''"
                @click="setRowHeight('160px')"
              >
                默认
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '130px' ? 'primary' : ''"
                @click="setRowHeight('130px')"
              >
                中等
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '100px' ? 'primary' : ''"
                @click="setRowHeight('100px')"
              >
                紧凑
              </el-button>
            </div>
            <template #reference>
              <el-button type="text" class="-button-spacustomcing">
                <el-icon><Grid /></el-icon>
              </el-button>
            </template>
          </el-popover>

          <!-- 列显示设置 -->
          <el-popover placement="bottom" width="100" v-model:visible="columnsDialogVisible">
          <div class="popover-content">
            <el-checkbox-group v-model="tempSelectedColumns">
              <el-checkbox
                v-for="column in columns"
                :key="column.prop"
                :label="column.prop"
              > 
                {{ column.label }}
              </el-checkbox>
            </el-checkbox-group>
            <div class="popover-footer">
              <el-button @click="resetColumns">重置</el-button>
              <el-button type="primary" @click="confirmColumns">确定</el-button>
            </div>
          </div>
          <template #reference>
            <el-button type="text">
              <el-icon><Setting /></el-icon>
            </el-button>
          </template>
        </el-popover>
        </div>
      </div>

      <!-- 表格区域 -->
      <el-table
        :data="tableData"
        style="width: 98.5%; margin-left: 10px; margin-right: 20px; margin-top: 10px;"
        :row-style="getRowStyle"
        :header-cell-style="{
        background: '#f5f7fa',
        color: '#909399',
        textAlign: 'center',
      }"
        :row-class-name="tableRowClassName"
      >
        <el-table-column
          v-for="column in visibleColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :width="column.width"
          :align="column.align || 'center'"
        >
          <template #default="scope">
            <template v-if="column.prop === 'displayIndex'">
              {{ scope.$index + 1 }}
            </template>
            <template v-else-if="column.prop === 'imageUrl'">
              <el-image
                style="width: 100px; height: 100px"
                :src="scope.row[column.prop]"
                :preview-src-list="[scope.row[column.prop]]"
                fit="contain"
                preview-teleported
              />
            </template>
            <template v-else-if="column.prop === 'operation'">
              <div class="operation-buttons">
                <el-button type="text" style="color: red" @click="deleteAdvertisement(scope.row.id)">
                  删除
                </el-button>
                <el-button type="text" style="color: blue" @click="editAdvertisement(scope.row)">
                  编辑
                </el-button>
              </div>
            </template>
            <template v-else>
              {{ scope.row[column.prop] }}
            </template>
          </template>
         
        </el-table-column>

        <template #empty>
          <div class="empty-state">
            <el-icon class="empty-icon"><Box /></el-icon>
            <p class="empty-text">暂无数据</p>
          </div>
        </template>
      </el-table>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="500px"
      class="custom-dialog"
      @close="closeDialog"
    >
      <div class="dialog-content">
        <el-form :model="formData" label-width="100px" :rules="rules" ref="formRef">
          <el-form-item label="标题" prop="title">
            <el-input v-model="formData.title" placeholder="请输入广告标题" />
          </el-form-item>
          <el-form-item label="图片" prop="imageUrl">
            <div class="image-upload-container">
              <el-upload
                class="image-upload"
                action="#"
                :auto-upload="false"
                :show-file-list="false"
                :on-change="handleImageChange"
              >
                <img
                  v-if="formData.imageUrl"
                  :src="formData.imageUrl"
                  class="preview-image"
                />
                <el-icon v-else class="upload-icon"><Plus /></el-icon>
              </el-upload>
              <div class="upload-right">
                <div class="upload-text">请上传素材图，大于800*800将会被压缩</div>
                <el-button
                  v-if="formData.imageUrl"
                  type="danger"
                  size="small"
                  @click="removeImage"
                  class="delete-button"
                >
                  删除图片
                </el-button>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="submitForm">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { ElMessage, ElLoading } from "element-plus";
import { Box, Plus, Refresh, Grid, Setting } from "@element-plus/icons-vue";
import { getAdvertisementList, deleteAdvertisement, createAdvertisement, updateAdvertisement } from "@/api/advertisement";
import { ImageCompressor } from "@/utils/imageCompressor";

export default {
  name: "AdvertisementPage",
  components: {
    Box,
    Plus,
    Refresh,
    Grid,
    Setting,
  },
  setup() {
    const tableData = ref([]);
    const loading = ref(false);
    const dialogVisible = ref(false);
    const dialogTitle = ref("新增广告");
    const formRef = ref(null);
    const formData = ref({
      id: "",
      title: "",
      imageUrl: "",
    });
    const userRole = ref(1);

    // 初始化时获取用户角色
    onMounted(() => {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        userRole.value = user.role; // 0: 管理员, 1: 员工, 2: 维修工程师
        
        // 非管理员角色隐藏操作列
        if (userRole.value === 1 || userRole.value === 2) {
          const index = selectedColumns.value.indexOf('operation');
          if (index > -1) {
            selectedColumns.value.splice(index, 1);
          }
        }
      }
      refreshData(false);
    });

    // 刷新数据
    const refreshData = async (isShow) => {
      loading.value = true;
      try {
        const response = await getAdvertisementList();
        if (response.data && response.success) {
          if(isShow)
            ElMessage.success("数据已刷新");
          tableData.value = response.data || [];
        } else {
          ElMessage.error(response?.message || "获取数据失败");
        }
      } catch (error) {
        console.error("刷新数据失败:", error);
        ElMessage.error(error.message || "刷新数据失败");
      } finally {
        loading.value = false;
      }
    };

    // 删除广告
    const deleteAdvertisementItem = async (id) => {
      try {
        const response = await deleteAdvertisement(id);
        if (response.success) {
          ElMessage.success("删除成功");
          await refreshData(false);
        } else {
          ElMessage.error(response?.message || "删除失败");
        }
      } catch (error) {
        console.error("删除失败:", error);
        ElMessage.error(error.message || "删除失败");
      }
    };

    // 提交表单
    const submitForm = async () => {
      if (!formRef.value) return;
      
      await formRef.value.validate(async (valid) => {
        if (valid) {
          try {
            const api = dialogTitle.value === "新增广告" ? createAdvertisement : updateAdvertisement;
            const response = await (dialogTitle.value === "新增广告" ? 
              api(formData.value) : 
              api(formData.value.id, formData.value));
            
            if (response.data && response.success) {
              ElMessage.success(dialogTitle.value === "新增广告" ? "添加成功" : "更新成功");
              closeDialog();
              await refreshData(false);
            } else {
              ElMessage.error(response?.message || (dialogTitle.value === "新增广告" ? "添加失败" : "更新失败"));
            }
          } catch (error) {
            console.error("提交失败:", error);
            ElMessage.error(error.message || "提交失败");
          }
        }
      });
    };

    // 编辑广告
    const editAdvertisement = (row) => {
      dialogTitle.value = "编辑广告";
      formData.value = { ...row };
      dialogVisible.value = true;
    };

    // 打开新增对话框
    const openAddDialog = () => {
      dialogTitle.value = "新增广告";
      formData.value = {
        title: "",
        imageUrl: "",
      };
      dialogVisible.value = true;
    };

    // 关闭对话框
    const closeDialog = () => {
      dialogVisible.value = false;
      formData.value = {
        id: "",
        title: "",
        imageUrl: "",
      };
      if (formRef.value) {
        formRef.value.resetFields();
      }
    };

    // 表格配置
    const columns = ref([
      { prop: "displayIndex", label: "序号", visible: true },
      { prop: "title", label: "标题", visible: true },
      { prop: "imageUrl", label: "图片", visible: true },
      { prop: "operation", label: "操作", visible: true },
    ]);

    const visibleColumns = computed(() =>
      columns.value.filter((col) => selectedColumns.value.includes(col.prop))
    );

    const selectedColumns = ref(columns.value.map((col) => col.prop));
    const tempSelectedColumns = ref([]);
    const rowHeight = ref("160px");
    const densityDialogVisible = ref(false);
    const columnsDialogVisible = ref(false);

    const rules = {
      title: [
        { required: true, message: "请输入标题", trigger: "blur" },
        { min: 2, max: 50, message: "长度在 2 到 50 个字符", trigger: "blur" },
      ],
      imageUrl: [{ required: true, message: "请上传图", trigger: "change" }],
    };

    // 表格样式
    const getRowStyle = () => {
      return { height: rowHeight.value };
    };

    const tableRowClassName = ({ rowIndex }) =>
      rowIndex % 2 === 0 ? "even-row" : "odd-row";

    // 处理图片上传
    const handleImageChange = async (file) => {
      const isImage = file.raw.type.startsWith("image/");
      if (!isImage) {
        ElMessage.error("请上传图片文件！");
        return;
      }

      const loading = ElLoading.service({
        lock: true,
        text: '图片处理中...',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      try {
        // 将File对象转换为base64
        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file.raw);
        });

        // 压缩图片
        const compressedBase64 = await ImageCompressor.compress(base64, {
          maxWidth: 800,
          maxHeight: 800,
          quality: 0.8
        });

        formData.value.imageUrl = compressedBase64;
        ElMessage.success("图片处理完成");
      } catch (error) {
        ElMessage.error("图片处理失败，请重试");
        console.error("图片处理错误:", error);
      } finally {
        loading.close();
      }
    };

    // 删除图片
    const removeImage = () => {
      formData.value.imageUrl = '';
      ElMessage.success("图片已删除");
    };

    // 设置表格行高
    const setRowHeight = (height) => {
      rowHeight.value = height;
      densityDialogVisible.value = false;
    };

    // 重置列显示
    const resetColumns = () => {
      // 根据用户角色决定是否显示操作列
      if (userRole.value === 1 || userRole.value === 2) {
        // 非管理员角色不显示操作列
        tempSelectedColumns.value = columns.value
          .filter(col => col.prop !== 'operation')
          .map(col => col.prop);
      } else {
        // 管理员显示所有列
        tempSelectedColumns.value = columns.value.map(col => col.prop);
      }
      columnsDialogVisible.value = false;
    };

    // 确认列显示设置
    const confirmColumns = () => {
      if (tempSelectedColumns.value.length === 0) {
        ElMessage.warning("至少选择一列");
        return;
      }
      
      // 非管理员角色强制移除操作列
      if (userRole.value === 1 || userRole.value === 2) {
        const index = tempSelectedColumns.value.indexOf('operation');
        if (index > -1) {
          tempSelectedColumns.value.splice(index, 1);
        }
      }
      
      selectedColumns.value = [...tempSelectedColumns.value];
      columnsDialogVisible.value = false;
    };

    onMounted(() => {
      tempSelectedColumns.value = [...selectedColumns.value];
    });

    return {
      tableData,
      loading,
      dialogVisible,
      dialogTitle,
      formRef,
      formData,
      rules,
      columns,
      visibleColumns,
      selectedColumns,
      tempSelectedColumns,
      rowHeight,
      densityDialogVisible,
      columnsDialogVisible,
      userRole,
      getRowStyle,
      tableRowClassName,
      openAddDialog,
      editAdvertisement,
      deleteAdvertisement: deleteAdvertisementItem,
      submitForm,
      closeDialog,
      refreshData,
      setRowHeight,
      resetColumns,
      confirmColumns,
      handleImageChange,
      removeImage,
    };
  },
};
</script>

<style scoped>
.my-devices {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.data-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
}

.toolbar {
  display: flex;
  gap: 8px;
}

.operation-buttons {
  gap: 8px;
}

.even-row {
  background-color: #fafafa;
}

.odd-row {
  background-color: #ffffff;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  color: #909399;
  margin-bottom: 16px;
}

.empty-text {
  color: #909399;
  margin: 0;
  font-size: 14px;
}

.popover-content {
  display: flex;
  flex-direction: column;
}

.popover-footer {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding: 10px;
}

.density-button {
  display: flex;
  justify-content: center;
  margin: 10px;
}

/* 图片上传样式 */
.image-upload-container {
  display: flex;
  align-items: center;
  gap: 20px;
}

.image-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 150px;
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.image-upload:hover {
  border-color: #409eff;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

.upload-icon {
  font-size: 28px;
  color: #8c939d;
  margin-bottom: 8px;
  cursor: pointer;
}

/* 确保整个上传区域都可点击 */
:deep(.el-upload) {
  width: 100%;
  height: 100%;
}

:deep(.el-upload .el-upload-dragger) {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-right {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 150px;
}

.upload-text {
  color: #606266;
  font-size: 12px;
  margin-bottom: 10px;
}

.delete-button {
  align-self: flex-start;
}
</style>